import { z } from 'zod';
/**
 * Central server contracts.
 *
 * The same codebase runs in two roles. An `edge` station inspects a line and
 * pushes what it learns upward; `central` collects from the fleet, trains on the
 * combined data and publishes models back down.
 *
 * The rule that shapes all of this: the line must never stop because central is
 * unreachable. Sync is therefore best-effort and resumable, and nothing in the
 * inspection path waits on it.
 */
export const StationSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    /** Which line it sits on — a site usually has several. */
    lineName: z.string().nullable().optional(),
    customerId: z.number().int().positive().nullable(),
    customer: z.string().nullable(),
    /** Pill tint for the owning customer — see customerColors.ts. */
    customerHue: z.number().int().nullable().default(null),
    /** Observed on each sync rather than configured, so it cannot drift. */
    lastIp: z.string().nullable().optional(),
    companyName: z.string().nullable().optional(),
    contactName: z.string().nullable().optional(),
    /** The build the station reports running — its own fact, not central's. */
    appCommit: z.string().nullable().optional(),
    updatePolicy: z.enum(['manual', 'auto', 'offline']).optional(),
    active: z.boolean(),
    /** Last time the station authenticated at all — the liveness signal. */
    lastSeenAt: z.string().nullable(),
    /** Last time it actually delivered rows. */
    lastSyncAt: z.string().nullable(),
    inspections: z.number().int().nonnegative(),
    annotations: z.number().int().nonnegative(),
});
export const SyncStreamSchema = z.object({
    stream: z.string(),
    lastSynced: z.string(),
    lastRunAt: z.string().nullable(),
    lastError: z.string().nullable(),
    pushedTotal: z.number().int().nonnegative(),
});
export const SyncStatusSchema = z.object({
    role: z.enum(['edge', 'central']),
    enabled: z.boolean(),
    url: z.string(),
    streams: z.array(SyncStreamSchema),
    /** Rows waiting to go up. Non-zero is normal; growing without bound is not. */
    pending: z.object({
        inspections: z.number().int().nonnegative(),
        annotations: z.number().int().nonnegative(),
    }),
});
/**
 * How stale a station's last contact is allowed to be before it is worth
 * looking at. A station syncs every few minutes, so an hour of silence means
 * something is wrong; a day means it is effectively offline.
 */
export function stationHealth(lastSeenAt, now = Date.now()) {
    if (!lastSeenAt)
        return 'offline';
    const age = now - new Date(lastSeenAt).getTime();
    if (age < 60 * 60 * 1000)
        return 'online';
    if (age < 24 * 60 * 60 * 1000)
        return 'late';
    return 'offline';
}
/** Bytes as something a person can read at a glance. */
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    const units = ['kB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
//# sourceMappingURL=fleet.js.map